/**
 * End crystal model (vanilla EndCrystalModel): outer glass, inner glass and the
 * core cube, each nested one level deeper in the tilted spin, bobbing above its
 * pillar, plus the pink healing beam to the dragon. Uses the real
 * entity/end_crystal sheets (model, bedrock base and beam).
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import { buildPart, entityMaterial, sheetTexture, type EntityMat } from './core';

const SCALE = 1 / 16; // the 26.x sheet already holds the 2x (16px) cubes

/** 26.x EndCrystalModel: 128x64 sheet, 16px glass cubes (glass at u 0, core at u 64) and a 24x8x24 bedrock base. */
const cube = (u: number, inflate = 0): Parameters<typeof buildPart>[0] => ({
  cubes: [{ tex: [u, 0], from: [-8, -8, -8], size: [16, 16, 16], inflate }],
});

export class CrystalModel {
  readonly root = new THREE.Group();
  readonly mat: EntityMat;
  private readonly spin = new THREE.Group();
  private readonly inner = new THREE.Group();
  private readonly core = new THREE.Group();
  private readonly base = new THREE.Group();
  private readonly tilt = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 1).normalize(), Math.PI / 3);

  constructor(env: EnvUniforms) {
    this.mat = entityMaterial(env, sheetTexture('entity/end_crystal/end_crystal'), { emissive: true });
    const wrap = new THREE.Group();
    wrap.scale.set(SCALE, SCALE, SCALE);
    const outer = buildPart(cube(0), 128, 64, this.mat);
    const inn = buildPart(cube(0), 128, 64, this.mat);
    inn.scale.setScalar(0.875);
    const core = buildPart(cube(64), 128, 64, this.mat);
    core.scale.setScalar(0.875 * 0.875);
    this.core.add(core);
    this.inner.add(inn, this.core);
    this.spin.add(outer, this.inner);
    wrap.add(this.spin);
    this.root.add(wrap);
    // bedrock base plate (y-down model space), kept still while the crystal bobs
    const plate = buildPart({ cubes: [{ tex: [0, 32], from: [-12, 0, -12], size: [24, 8, 24] }] }, 128, 64, this.mat);
    this.base.scale.set(-1 / 16, -1 / 16, 1 / 16);
    this.base.add(plate);
    this.base.position.y = -0.75;
    this.root.add(this.base);
  }

  /** `age` in ticks (+ partial), vanilla spin 3 degrees per tick and the sine bob. */
  pose(age: number): number {
    const ry = age * 3 * (Math.PI / 180);
    this.spin.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), ry).multiply(this.tilt);
    this.inner.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), ry).multiply(this.tilt);
    this.core.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), ry).multiply(this.tilt);
    const h = Math.sin(age * 0.2) / 2 + 0.5;
    const bob = (h * h + h) * 0.4; // bob height in blocks, 0..0.8
    this.base.position.y = -0.75 - bob;
    return bob;
  }

  dispose(): void {
    this.mat.dispose();
  }
}

/** Soft additive beam cylinder from a crystal to the dragon. */
export class BeamPool {
  readonly group = new THREE.Group();
  private readonly geo = new THREE.CylinderGeometry(0.3, 0.3, 1, 8, 1, true);
  /** The real entity/end_crystal/end_crystal_beam.png strip, scrolled along the beam. */
  private readonly tex = sheetTexture('entity/end_crystal/end_crystal_beam');
  private readonly mat: THREE.MeshBasicMaterial;
  private readonly meshes: THREE.Mesh[] = [];
  private used = 0;

  constructor() {
    this.tex.wrapS = this.tex.wrapT = THREE.RepeatWrapping;
    this.mat = new THREE.MeshBasicMaterial({ map: this.tex, side: THREE.DoubleSide, alphaTest: 0.1 });
  }

  begin(): void { this.used = 0; }

  add(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, time: number): void {
    let m = this.meshes[this.used];
    if (!m) { m = new THREE.Mesh(this.geo, this.mat); m.frustumCulled = false; this.meshes.push(m); this.group.add(m); }
    this.used++;
    m.visible = true;
    const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
    const len = Math.hypot(dx, dy, dz) || 1;
    m.position.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(dx / len, dy / len, dz / len));
    m.rotateY(time * 0.3);
    this.tex.offset.y = -time * 0.02;
    this.tex.repeat.y = 4;
    m.scale.set(1 + Math.sin(time * 0.7) * 0.25, len, 1 + Math.sin(time * 0.7) * 0.25);
  }

  end(): void { for (let i = this.used; i < this.meshes.length; i++) this.meshes[i].visible = false; }

  dispose(): void { this.geo.dispose(); this.mat.dispose(); }
}
